/**
 * Per-category PnL for profile pages, scoped to the viewer.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { createApiClient } from 'lib/api/createApiClient';
import type { SocialProfileCategoryMetricsVo } from 'models';
import type { GetSocialProfileCategoryMetricsResponse } from 'models/reqres';
import config from 'utils/config';

import type { LeaderboardMetricName } from './socialLeaderboard';
import { useSocialProfileSurfaceScope, type SocialProfileSurfaceScope } from './socialProfileHoldings';
import { parseVisibilityState, type SurfaceVisibility } from './socialProfileVisibility';
import { removeEmpty } from './utils';

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

const STALE_TIME = 60 * 1000;
const GC_TIME = 5 * 60 * 1000;

export interface SocialCategoryPnlData {
    metrics: SocialProfileCategoryMetricsVo[];
    visibilityState: SurfaceVisibility;
}

export const socialCategoryPnlKeys = {
    all: ['socialCategoryPnl'] as const,
    byNickname: (nickname: string, metric: LeaderboardMetricName, scope: SocialProfileSurfaceScope) =>
        [
            ...socialCategoryPnlKeys.all,
            metric,
            removeEmpty({ nickname, scope: scope.scope, viewerUserId: scope.viewerUserId }),
        ] as const,
};

async function fetchSocialCategoryPnl(
    nickname: string,
    metric: LeaderboardMetricName,
    scope: SocialProfileSurfaceScope,
    signal?: AbortSignal,
): Promise<SocialCategoryPnlData> {
    if (scope.scope === 'own' && scope.viewerUserId) {
        const response = await api.get<GetSocialProfileCategoryMetricsResponse>(
            `users/${scope.viewerUserId}/social/profile/subcategory_metrics`,
            { params: { metric_name: metric }, signal },
        );
        return { metrics: response.data.rank_list, visibilityState: 'visible' };
    }

    const path =
        scope.scope === 'profile_view' && scope.viewerUserId
            ? `users/${scope.viewerUserId}/social/profile_view/category_metric`
            : 'social/profile/category_metric';
    const response = await api.get<GetSocialProfileCategoryMetricsResponse>(path, {
        params: removeEmpty({ nickname, metric_name: metric }),
        signal,
    });
    return {
        metrics: response.data.rank_list,
        visibilityState: parseVisibilityState(response.data.visibility_state),
    };
}

/**
 * Per-category metrics for a profile, scoped to the viewer relationship.
 */
export function useSocialCategoryPnl(
    nickname: string,
    metric: LeaderboardMetricName,
    options?: { enabled?: boolean },
): UseQueryResult<SocialCategoryPnlData, Error> {
    const { scope, ready } = useSocialProfileSurfaceScope(nickname);
    return useQuery({
        queryKey: socialCategoryPnlKeys.byNickname(nickname, metric, scope),
        queryFn: ({ signal }) => fetchSocialCategoryPnl(nickname, metric, scope, signal),
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        retry: 2,
        enabled: !!nickname && ready && (options?.enabled ?? true),
    });
}
